import React, { useMemo } from "react";
import { Box, MenuItem, Select, Chip, Typography, alpha, Skeleton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import { useGetFinanceLocationsQuery } from "../../Api/attoDeskApi";

/**
 * Location filter for Finance pages. State lives in the URL (`?location_id=`)
 * so it survives refreshes, back/forward, and shared links across the finance
 * subpages (Overview, Invoices, Transactions, Expenses, Reports).
 *
 * Value contract:
 *   - "all"  → All Locations (default)
 *   - "0"    → Online / Unassigned bucket (null location on the record)
 *   - "<id>" → specific location_tables.id
 */
export function useLocationFilter(): {
  locationId: number | "all";
  setLocationId: (v: number | "all") => void;
} {
  const [params, setParams] = useSearchParams();
  const raw = params.get("location_id");
  const locationId: number | "all" = raw === null || raw === "all" ? "all" : Number(raw);
  const setLocationId = (v: number | "all") => {
    const next = new URLSearchParams(params);
    if (v === "all") next.delete("location_id");
    else next.set("location_id", String(v));
    setParams(next, { replace: true });
  };
  return { locationId, setLocationId };
}

/** Convert the URL value to the shape backend endpoints expect. */
export function toLocationParam(v: number | "all"): { location_id?: number | "all" } {
  return v === "all" ? {} : { location_id: v };
}

const LocationFilter: React.FC = () => {
  const { data, isLoading } = useGetFinanceLocationsQuery();
  const { locationId, setLocationId } = useLocationFilter();

  const payload = (data?.data as any) || {};
  const locations: { id: number; name: string }[] = payload.locations || [];
  const canPickAll: boolean = payload.canPickAll ?? true;
  const defaultLocationId: number | null = payload.defaultLocationId ?? null;

  // Users locked to a single location: force the URL to their id on first render.
  React.useEffect(() => {
    if (!isLoading && !canPickAll && defaultLocationId && locationId === "all") {
      setLocationId(defaultLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, canPickAll, defaultLocationId]);

  const activeLabel = useMemo(() => {
    if (locationId === "all") return "All Locations";
    const found = locations.find(l => l.id === locationId);
    return found?.name || (locationId === 0 ? "Online / Unassigned" : `Location #${locationId}`);
  }, [locationId, locations]);

  if (isLoading) return <Skeleton variant="rounded" width={220} height={38} />;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Select
        size="small"
        value={locationId === "all" ? "all" : String(locationId)}
        onChange={e => {
          const v = e.target.value;
          setLocationId(v === "all" ? "all" : Number(v));
        }}
        startAdornment={<PlaceIcon sx={{ fontSize: 18, mr: 0.5, color: "text.disabled" }} />}
        sx={{
          minWidth: 220, fontSize: 13, fontWeight: 600, bgcolor: "#fff",
          borderRadius: "10px",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
        }}
      >
        {canPickAll && <MenuItem value="all">All Locations</MenuItem>}
        {locations.map(l => (
          <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>
        ))}
      </Select>
      {locationId !== "all" && (
        <Chip
          size="small"
          icon={<PlaceIcon sx={{ fontSize: "14px !important" }} />}
          label={`Viewing: ${activeLabel}`}
          onDelete={canPickAll ? () => setLocationId("all") : undefined}
          deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
          sx={{
            height: 26, fontSize: 12, fontWeight: 600,
            bgcolor: alpha("#6366f1", 0.08), color: "#6366f1",
            "& .MuiChip-icon": { color: "#6366f1" },
          }}
        />
      )}
      <Typography sx={{ display: "none" }}>{activeLabel}</Typography>
    </Box>
  );
};

export default LocationFilter;
